/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  Building2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Plus,
  RefreshCw,
  Archive,
  ExternalLink,
  Loader2,
  HardHat,
  ShieldCheck,
  Hourglass,
  FileSignature,
  FolderArchive,
  FileText,
  Upload,
  Scale,
  Calculator,
} from 'lucide-react';



export const CcmiDetailDialog = ({ ctx }) => {
  const {
    analyzingDocId, archiving, assigningJuriste, autosaveAt, autosaveError, autosaveStatus,
    autosaveTick, complianceDraft, complianceLoading, complianceSaving, compliancingDoc, counts,
    creating, creatingFinalDevis, draft, filterStatut, finalContractPaywall,
    generatingFinalContract, generatingPdf, importingPdf, items, juristeAssignLoading,
    juristeCommentInput, juristeEmailInput, loadError, loading, reportingDocId, reviewingDocId,
    search, selected, showCreate, syncing,
    setAnalyzingDocId, setArchiving, setAssigningJuriste, setAutosaveAt, setAutosaveError,
    setAutosaveStatus, setAutosaveTick, setComplianceDraft, setComplianceLoading, setComplianceSaving,
    setCompliancingDoc, setCounts, setCreating, setCreatingFinalDevis, setDraft,
    setFilterStatut, setFinalContractPaywall, setGeneratingFinalContract, setGeneratingPdf,
    setImportingPdf, setItems, setJuristeAssignLoading, setJuristeCommentInput, setJuristeEmailInput,
    setLoadError, setLoading, setReportingDocId, setReviewingDocId, setSearch, setSelected,
    setShowCreate, setSyncing,
    autosaveBaselineRef, autosaveTimerRef, fetchList, fetchedOnce, fileInputRef, handleAnalyzeDoc,
    handleArchive, handleCreate, handleCreateFinalContractDevis, handleDownloadReviewReport,
    handleGenerateFinalContract, handleGeneratePdf, handleImportPdf, handleReviewAi, handleSync,
    navigate, openComplianceModal, openJuristeAssign, saveComplianceDraft, submitComplianceDraft,
    submitJuristeAssign, triggerAutosave,
    STATUT_META, GUARD_STATUS_META, getAuthHeader, CCMI_COMPLIANCE_CHECKLIST, computeComplianceStatus,
    ComplianceStatusPanel, AutosaveIndicator,
  } = ctx;
  return (
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ccmi-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HardHat className="h-5 w-5 text-indigo-600" />
                  {selected.reference}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <Badge className={`${STATUT_META[selected.statut]?.color} border`}>
                      {STATUT_META[selected.statut]?.label || selected.statut}
                    </Badge>
                    {selected.domium_reference && (
                      <Badge className="bg-blue-700 text-white border-blue-900 font-semibold">
                        DOS DOMIUM : {selected.domium_reference}
                      </Badge>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-semibold text-slate-700 mb-1">Maître d&apos;ouvrage</div>
                  <div className="text-slate-600">
                    {selected.maitre_ouvrage?.nom || '-'} {selected.maitre_ouvrage?.prenom || ''}<br/>
                    <span className="text-xs">{selected.maitre_ouvrage?.email || ''}</span>
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-slate-700 mb-1">Constructeur</div>
                  <div className="text-slate-600">
                    {selected.constructeur?.raison_sociale || '-'}<br/>
                    <span className="text-xs">SIRET : {selected.constructeur?.siret || '-'}</span>
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-slate-700 mb-1">Terrain</div>
                  <div className="text-slate-600">{selected.terrain?.adresse || '-'}</div>
                </div>
                <div>
                  <div className="font-semibold text-slate-700 mb-1">Prix TTC</div>
                  <div className="text-slate-900 font-medium">
                    {selected.prix_ttc ? `${Number(selected.prix_ttc).toLocaleString('fr-FR')} €` : '-'}
                  </div>
                </div>
                {/* V38.19 — Lancement du simulateur CAP BATI+ CMI pré-rempli */}
                <div className="col-span-2 pt-2 border-t">
                  <a
                    href={`/simulateurs/cap-bati-plus-cmi?embed=1&projectId=${encodeURIComponent(selected.id)}&projectRef=${encodeURIComponent(selected.reference)}&ht=${encodeURIComponent(selected.prix_ttc ? (Number(selected.prix_ttc) / 1.085).toFixed(2) : '')}&ttc=${encodeURIComponent(selected.prix_ttc || '')}&type=${encodeURIComponent(selected.type_contrat || 'avec_plan')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md text-sm font-semibold shadow"
                    data-testid="ccmi-open-cap-bati-simulator"
                  >
                    <Calculator className="h-4 w-4" />
                    Simulateur CAP BATI+ CMI (pré-rempli)
                  </a>
                  <p className="text-xs text-slate-500 mt-1">
                    Coût HT estimé = TTC / 1,085 (TVA 8,5 % défaut DOM). Modifiable dans le simulateur.
                  </p>
                </div>
              </div>

              {/* Compliance guards */}
              <div>
                <div className="font-semibold text-slate-700 mb-2 mt-3">Garde-fous CCMI</div>
                <div className="space-y-1" data-testid="ccmi-guards">
                  {(selected.compliance_guards || []).map((g) => {
                    const meta = GUARD_STATUS_META[g.status] || GUARD_STATUS_META.missing;
                    const Icon = meta.Icon;
                    return (
                      <div key={g.key} className="flex items-start gap-2 text-sm py-1 border-b border-slate-100 last:border-0">
                        <Icon className={`h-4 w-4 mt-0.5 ${meta.color}`} />
                        <div className="flex-1">
                          <div className="text-slate-800">{g.label}</div>
                          {g.comment && <div className="text-xs text-slate-500">{g.comment}</div>}
                        </div>
                        <Badge className={`text-[10px] capitalize ${meta.badge || 'bg-slate-700 text-white border-slate-900 font-semibold'}`}>
                          {g.status.replace('_', ' ')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Historique sync */}
              <div>
                <div className="font-semibold text-slate-700 mb-2 mt-3">Historique synchronisation DOS DOMIUM</div>
                {(selected.domium_sync || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucune synchronisation effectuée.</p>
                ) : (
                  <div className="space-y-1 text-xs" data-testid="ccmi-sync-history">
                    {(selected.domium_sync || []).slice().reverse().map((s) => {
                      const isInbound = s.mode === 'webhook_inbound';
                      const promoted = s.promoted_to_signe;
                      return (
                        <div key={s.attempt_id} className={`flex items-center gap-2 p-2 rounded ${isInbound ? 'bg-indigo-50 border border-indigo-100' : 'bg-slate-50'}`}>
                          {s.status === 'success' ? (
                            <CheckCircle2 className={`h-4 w-4 ${promoted ? 'text-emerald-700' : 'text-emerald-600'}`} />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span className="font-mono text-slate-700">{s.timestamp?.slice(0, 19).replace('T', ' ')}</span>
                          {isInbound && (
                            <Badge className="text-[10px] bg-indigo-700 text-white border-indigo-900 font-semibold">
                              DOMIUM → CCMI
                            </Badge>
                          )}
                          <span className="text-slate-600">{s.event || s.action || s.status}</span>
                          {promoted && (
                            <Badge className="text-[10px] bg-emerald-700 text-white border-emerald-900 font-semibold">
                              → signé
                            </Badge>
                          )}
                          <span className="text-slate-600 ml-auto font-medium">{s.actor}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* V37.54-A — Documents importes (PDFs externes attaches au projet) */}
              {(selected.documents || []).length > 0 && (
                <div>
                  <div className="font-semibold text-slate-700 mb-2 mt-3">Documents importés</div>
                  <div className="space-y-1 text-xs" data-testid="ccmi-imported-docs">
                    {(selected.documents || []).slice().reverse().map((d) => (
                      <div key={d.document_id} className="p-2 bg-amber-50 rounded border border-amber-100 space-y-1" data-testid={`ccmi-doc-${d.document_id}`}>
                        {/* V37.56 — Badge statut juriste + bouton assigner (uniquement compliance_draft_pdf) */}
                        {d.kind === 'compliance_draft_pdf' && (
                          <div className="flex items-center gap-2 flex-wrap mb-1" data-testid={`ccmi-juriste-row-${d.document_id}`}>
                            <Badge className="text-[10px] bg-red-700 text-white border-red-900 font-semibold">Projet mise en conformité</Badge>
                            {d.juridique_review ? (
                              <Badge className={`text-[10px] ${
                                d.juridique_review.status === 'valide' ? 'bg-emerald-700 text-white border-emerald-900 font-semibold' :
                                d.juridique_review.status === 'refuse' ? 'bg-red-700 text-white border-red-900 font-semibold' :
                                d.juridique_review.status === 'a_retravailler' ? 'bg-orange-600 text-white border-orange-800 font-semibold' :
                                'bg-amber-600 text-white border-amber-800 font-semibold'
                              }`} data-testid={`ccmi-juriste-status-${d.document_id}`}>
                                Juriste : {d.juridique_review.status.replace(/_/g, ' ')}
                              </Badge>
                            ) : (
                              <Badge className="text-[10px] bg-slate-600 text-white border-slate-800 font-semibold">
                                Juriste : non assigné
                              </Badge>
                            )}
                            {d.juridique_review?.assigned_to && (
                              <span className="text-[10px] text-slate-500">→ {d.juridique_review.assigned_to}</span>
                            )}
                            <Button size="sm" variant="outline" className="h-5 px-2 text-[10px] ml-auto"
                              onClick={() => openJuristeAssign(d)}
                              data-testid={`ccmi-juriste-assign-btn-${d.document_id}`}>
                              {d.juridique_review ? 'Réassigner' : 'Assigner juriste'}
                            </Button>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-amber-700" />
                          <span className="font-mono text-slate-700">{d.document_id}</span>
                          <span className="text-slate-600">{d.filename}</span>
                          <Badge className={`text-[10px] ${
                            d.extraction_status === 'success'
                              ? 'bg-emerald-700 text-white border-emerald-900 font-semibold'
                              : d.requires_ocr
                                ? 'bg-orange-600 text-white border-orange-800 font-semibold'
                                : 'bg-slate-600 text-white border-slate-800 font-semibold'
                          }`}>
                            {d.extraction_status}
                          </Badge>
                          {d.requires_ocr && (
                            <Badge className="text-[10px] bg-orange-600 text-white border-orange-800 font-semibold">OCR requis</Badge>
                          )}
                          <span className="text-slate-400 ml-auto">{d.pages_count}p • {d.uploaded_by}</span>
                          {/* V37.54-B — Bouton Analyse IA */}
                          {!d.requires_ocr && d.extraction_status === 'success' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px]"
                              onClick={() => handleAnalyzeDoc(selected, d)}
                              disabled={analyzingDocId === d.document_id}
                              data-testid={`ccmi-analyze-btn-${d.document_id}`}
                              title="Analyse IA juridique (Claude Sonnet) — aide à la revue, non avis juridique"
                            >
                              {analyzingDocId === d.document_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <ShieldCheck className="h-3 w-3 mr-1" />
                              )}
                              {d.ai_analysis ? 'Re-analyser' : 'Analyser (IA)'}
                            </Button>
                          )}
                          {/* V37.54-D — Bouton telechargement rapport de revue */}
                          {d.ai_analysis && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px]"
                              onClick={() => handleDownloadReviewReport(selected, d)}
                              disabled={reportingDocId === d.document_id}
                              data-testid={`ccmi-review-report-${d.document_id}`}
                              title="Télécharger le rapport PDF de revue (analyse IA + décision humaine)"
                            >
                              {reportingDocId === d.document_id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <FileText className="h-3 w-3 mr-1" />
                              )}
                              Rapport
                            </Button>
                          )}
                          {/* V37.55 — Bouton Mise en conformite */}
                          {d.ai_analysis && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px] bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                              onClick={() => openComplianceModal(selected, d)}
                              data-testid={`ccmi-compliance-btn-${d.document_id}`}
                              title="Générer un projet de mise en conformité (validation juridique requise)"
                            >
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              Mise en conformité
                            </Button>
                          )}
                        </div>
                        {/* Resultat IA */}
                        {d.ai_analysis && d.ai_analysis.status === 'success' && d.ai_analysis.result && (
                          <div className="ml-6 mt-1 p-2 bg-white rounded border border-indigo-100 text-[11px] space-y-1" data-testid={`ccmi-ai-result-${d.document_id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="text-[10px] bg-indigo-700 text-white border-indigo-900 font-semibold">
                                Score : {d.ai_analysis.result.score_conformite}/100
                              </Badge>
                              <Badge className={`text-[10px] ${
                                d.ai_analysis.result.risque_global === 'eleve'
                                  ? 'bg-red-700 text-white border-red-900 font-semibold'
                                  : d.ai_analysis.result.risque_global === 'moyen'
                                    ? 'bg-orange-600 text-white border-orange-800 font-semibold'
                                    : 'bg-emerald-700 text-white border-emerald-900 font-semibold'
                              }`}>
                                Risque : {d.ai_analysis.result.risque_global}
                              </Badge>
                              <span className="text-slate-500">{d.ai_analysis.model}</span>
                            </div>
                            {d.ai_analysis.result.resume && (
                              <p className="text-slate-700 italic">&laquo; {d.ai_analysis.result.resume} &raquo;</p>
                            )}
                            {d.ai_analysis.result.manquements?.length > 0 && (
                              <div>
                                <span className="font-semibold text-red-700">Manquements ({d.ai_analysis.result.manquements.length}) :</span>
                                <ul className="list-disc ml-5 text-slate-600">
                                  {d.ai_analysis.result.manquements.slice(0, 5).map((m, i) => <li key={i}>{m}</li>)}
                                </ul>
                              </div>
                            )}
                            <p className="text-[9px] text-amber-700 italic mt-1">
                              ⚠ {d.ai_analysis.disclaimer}
                            </p>
                            {/* V37.54-C — Revue humaine post-IA */}
                            <div className="border-t border-indigo-100 pt-2 mt-2" data-testid={`ccmi-ai-review-${d.document_id}`}>
                              {d.ai_analysis_review ? (
                                <div className="text-[10px]">
                                  <Badge className={`${
                                    d.ai_analysis_review.decision === 'pret_pour_validation_juridique'
                                      ? 'bg-emerald-700 text-white border-emerald-900 font-semibold'
                                      : d.ai_analysis_review.decision === 'acceptable'
                                        ? 'bg-blue-700 text-white border-blue-900 font-semibold'
                                        : 'bg-amber-600 text-white border-amber-800 font-semibold'
                                  }`}>
                                    Revue : {d.ai_analysis_review.decision.replace(/_/g, ' ')}
                                  </Badge>
                                  <span className="text-slate-500 ml-2">
                                    par {d.ai_analysis_review.reviewed_by} • {d.ai_analysis_review.reviewed_at?.slice(0, 16).replace('T', ' ')}
                                  </span>
                                  {d.ai_analysis_review.comment && (
                                    <p className="text-slate-700 mt-1">&laquo; {d.ai_analysis_review.comment} &raquo;</p>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-wrap gap-1" data-testid={`ccmi-review-actions-${d.document_id}`}>
                                  <span className="text-[10px] text-slate-500 mr-1">Revue humaine :</span>
                                  <Button size="sm" variant="outline" className="h-5 px-2 text-[10px]"
                                    onClick={() => handleReviewAi(selected, d, 'a_corriger')}
                                    disabled={reviewingDocId === d.document_id}
                                    data-testid={`ccmi-review-correct-${d.document_id}`}>
                                    À corriger
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-5 px-2 text-[10px]"
                                    onClick={() => handleReviewAi(selected, d, 'acceptable')}
                                    disabled={reviewingDocId === d.document_id}
                                    data-testid={`ccmi-review-ok-${d.document_id}`}>
                                    Acceptable
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-5 px-2 text-[10px] bg-emerald-50"
                                    onClick={() => handleReviewAi(selected, d, 'pret_pour_validation_juridique')}
                                    disabled={reviewingDocId === d.document_id}
                                    data-testid={`ccmi-review-ready-${d.document_id}`}>
                                    Prêt validation juridique
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {d.ai_analysis && d.ai_analysis.status === 'error' && (
                          <p className="ml-6 text-[10px] text-red-600 italic">
                            Erreur analyse : {d.ai_analysis.error_message}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  data-testid="ccmi-import-pdf-input"
                  onChange={(e) => handleImportPdf(selected, e.target.files?.[0])}
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={importingPdf}
                  data-testid="ccmi-import-pdf-btn"
                  title="Importer un PDF de contrat existant (max 20 MB)"
                >
                  {importingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                  Importer PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleGeneratePdf(selected)}
                  disabled={generatingPdf}
                  data-testid="ccmi-generate-pdf-btn"
                  title="Générer un PDF projet de contrat (validation juridique requise)"
                >
                  {generatingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
                  Générer contrat (PDF)
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleArchive(selected)}
                  disabled={archiving || selected.statut === 'archive'}
                  data-testid="ccmi-archive-btn"
                >
                  {archiving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Archive className="h-4 w-4 mr-1" />}
                  Archiver
                </Button>
                <Button
                  onClick={() => handleGenerateFinalContract(selected)}
                  disabled={generatingFinalContract}
                  variant="default"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="ccmi-generate-final-contract-btn"
                >
                  {generatingFinalContract ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
                  Générer contrat définitif
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/ccmi/projects/${selected.id}/simulator-prefill`, { headers: getAuthHeader() });
                      const key = res.data?.project_id;
                      const prefill = res.data?.prefill;
                      if (!key || !prefill) {
                        toast.error('Impossible de récupérer le prefill');
                        return;
                      }
                      sessionStorage.setItem(`domium_prefill_${key}`, JSON.stringify(prefill));
                      const ref = encodeURIComponent(res.data?.reference || '');
                      window.open(`/simulateur-dos?prefill=true&from_ccmi=${key}&ccmi_ref=${ref}`, '_blank');
                      toast.success('Simulateur DOS DOMIUM ouvert avec le contrat pré-rempli');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Erreur pré-remplissage');
                    }
                  }}
                  variant="outline"
                  className="border-violet-700 text-violet-900 hover:bg-violet-50"
                  data-testid="ccmi-prefill-simulator-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Pré-remplir Simulateur DOS DOMIUM
                </Button>
                <Button
                  onClick={() => handleSync(selected)}
                  disabled={syncing || selected.statut === 'archive'}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="ccmi-sync-btn"
                >
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ExternalLink className="h-4 w-4 mr-1" />}
                  Synchroniser DOS DOMIUM
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
  );
};

export default CcmiDetailDialog;
